import { isValid, parseISO } from "date-fns"

import type { BooleanRecord } from "@p-ban/common/src/types"
import { parseYearMonthDay } from "@p-ban/common/src/utils/DateUtils"
import { parseNumber } from "@p-ban/common/src/utils/NumberUtils"

import getConstantByValue from "../../service/constant/getConstantByValue"
import { isBooleanField, isDateField, isNumberField } from "../../service/estimateFields/estimateField"
import getInitialValue from "../../service/getInitialValue"
import type EstimateValues from "../../types/params"
import { convertForClient, convertForServerByParams } from "./converter"
import type { EstimateParams } from "../../types/params"

/**
 * 入力値をURLのパラメーターに変換
 */
export const createShareLink = (params: EstimateParams) => {
  const url = new URL(window.location.pathname, window.location.origin)

  for (const [key, value] of Object.entries(convertForServerByParams(params))) {
    if (value !== null) {
      url.searchParams.append(key, value instanceof Date ? value.toISOString() : value.toString())
    }
  }

  return url.toString()
}

/**
 * URLから入力状態を復元
 * - localStorageMapkeyについては、アップロードエリアでクエリパラメータから直接取得するため、ここでは取得しない
 * @see PartsListUpload.tsx
 */
export const getValuesFromShareLink = (queryString: string) => {
  const params = convertForClient(queryString)
  const entries = Array.from(params.entries())

  const fields = new Set(Object.keys(getInitialValue))
  const isEstimateField = (field: string): field is keyof EstimateValues => fields.has(field)

  const values: Partial<EstimateValues> = {}
  const unknownParams: Partial<BooleanRecord<EstimateValues>> = {}
  let bomSnapshotId: string | null = null
  let deliveryZeroFlg = false
  let mountQuickFlg = false
  let metalMaskStockPartName: string | null = null
  let boardStockPartName: string | null = null

  entries.forEach(([name, value]) => {
    // GUGEN Hub連携のパラメータは別物として扱う
    if (name === "bomSnapshotId") {
      bomSnapshotId = value
      return
    }
    if (name === "deliveryZeroFlg") {
      deliveryZeroFlg = value.toLowerCase() === "true"
      return
    }
    if (name === "mountQuickFlg") {
      mountQuickFlg = value.toLowerCase() === "true"
      return
    }
    if (name === "metalMaskStockPartName") {
      metalMaskStockPartName = value
      return
    }
    if (name === "boardStockPartName") {
      boardStockPartName = value
      return
    }
    if (!isEstimateField(name)) return

    if (isBooleanField(name)) {
      if (value.toLowerCase() === "true") {
        values[name] = true
      } else if (value.toLowerCase() === "false") {
        values[name] = false
      } else {
        unknownParams[name] = true
      }
      return
    }

    if (isNumberField(name)) {
      if (value !== "") {
        const num = parseNumber(value, null)
        if (num === null) {
          unknownParams[name] = true
        } else {
          values[name] = num
        }
      }

      return
    }

    if (isDateField(name)) {
      const date = parseISO(value)
      if (isValid(date)) {
        values[name] = date
      } else {
        // 古いフォーマットの変換
        // 互換性のために残しています。
        try {
          values[name] = parseYearMonthDay(value)
        } catch {
          unknownParams[name] = true
        }
      }

      return
    }

    try {
      values[name] = getConstantByValue(name, value).value
    } catch {
      if (value !== "") {
        unknownParams[name] = true
      }
    }
  })

  return {
    values,
    unknownParams,
    bomSnapshotId,
    deliveryZeroFlg,
    mountQuickFlg,
    metalMaskStockPartName,
    boardStockPartName,
  }
}
